import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Volume2, VolumeX, Gift, Scissors, Sparkles, Beer, Coffee, Wine } from 'lucide-react';
export const giftCards = [{
  id: 1,
  title: 'Cortes Old School + Arreglo de Barba',
  desc: 'El ritual completo. Corte clásico y arreglo de barba con toalla caliente.',
  icon: Scissors,
  price: '$25.000'
}, {
  id: 2,
  title: 'Afeitado Tradicional',
  desc: 'Navaja, toalla caliente y productos premium. Piel perfecta, sin irritación.',
  icon: Sparkles,
  price: '$18.000'
}, {
  id: 3,
  title: 'Rapado + Afeitado Tradicional',
  desc: 'Un look limpio y definido. Rapado a máquina y afeitado clásico.',
  icon: Scissors,
  price: '$22.000'
}, {
  id: 4,
  title: 'Getting Ready — Servicio para el Novio',
  desc: 'Cerramos el salón para vos. Tragos, música y el mejor look para tu gran día.',
  icon: Wine,
  price: 'A convenir',
  isConsult: true
}, {
  id: 5,
  title: 'Afeitado Tradicional + Birra',
  desc: 'Relajate con una cerveza fría mientras disfrutás del afeitado clásico.',
  icon: Beer,
  price: '$21.000'
}, {
  id: 6,
  title: 'Cortes Old School + Arreglo de Barba + Café',
  desc: 'El servicio completo acompañado de un café de especialidad.',
  icon: Coffee,
  price: '$28.000'
}];
const leatherStyle: React.CSSProperties = {
  background: `
    repeating-linear-gradient(
      45deg,
      rgba(255,255,255,0.015) 0px,
      rgba(255,255,255,0.015) 1px,
      transparent 1px,
      transparent 8px
    ),
    repeating-linear-gradient(
      -45deg,
      rgba(255,255,255,0.015) 0px,
      rgba(255,255,255,0.015) 1px,
      transparent 1px,
      transparent 8px
    ),
    linear-gradient(160deg, #1a1008 0%, #0d0d0d 40%, #1a1008 100%)
  `
};
export function GiftCards() {
  const [isMuted, setIsMuted] = useState(true);
  const [hasInteracted, setHasInteracted] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  useEffect(() => {
    const handleInteraction = () => {
      if (!hasInteracted) {
        setHasInteracted(true);
        setIsMuted(false);
        if (iframeRef.current?.contentWindow) {
          iframeRef.current.contentWindow.postMessage('{"event":"command","func":"unMute","args":""}', '*');
          iframeRef.current.contentWindow.postMessage('{"event":"command","func":"setVolume","args":[20]}', '*');
        }
      }
    };
    window.addEventListener('click', handleInteraction, {
      once: true
    });
    return () => window.removeEventListener('click', handleInteraction);
  }, [hasInteracted]);
  useEffect(() => {
    if (iframeRef.current?.contentWindow) {
      const command = isMuted ? 'mute' : 'unMute';
      iframeRef.current.contentWindow.postMessage(`{"event":"command","func":"${command}","args":""}`, '*');
    }
  }, [isMuted]);
  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMuted(!isMuted);
    setHasInteracted(true);
  };
  return <div className="min-h-screen bg-[#0D1F18] text-rotterdam-cream font-sans antialiased" data-id="element-233">
      {/* Hidden YouTube Audio */}
      <iframe ref={iframeRef} src="https://www.youtube.com/embed/-Z2lTeSIQIw?enablejsapi=1&autoplay=1&mute=1&loop=1&playlist=-Z2lTeSIQIw&controls=0" allow="autoplay" title="bg-audio" style={{
      position: 'fixed',
      top: '-9999px',
      left: '-9999px',
      width: '1px',
      height: '1px'
    }} data-id="element-234" />

      {/* HERO */}
      <section className="relative min-h-screen flex flex-col items-center justify-center px-6 overflow-hidden" style={{
      background: 'radial-gradient(ellipse at center, #122B20 0%, #0A1A14 70%)'
    }} data-id="element-235">
        {/* Volume icon */}
        <button onClick={toggleMute} className="absolute top-8 right-8 z-50 p-3 rounded-full bg-black/20 hover:bg-black/40 border border-white/10 text-white/50 hover:text-white transition-all duration-300 group" title="Sonido" data-id="element-236">
          {isMuted ? <VolumeX className="w-5 h-5" data-id="element-237" /> : <Volume2 className="w-5 h-5" data-id="element-238" />}
          <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap bg-black/80 px-2 py-1 rounded" data-id="element-239">
            Sonido
          </span>
        </button>

        <div className="max-w-4xl mx-auto text-center z-10" data-id="element-240">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-241">
            <Gift className="w-14 h-14 mx-auto mb-8 text-rotterdam-gold pearl-icon" data-id="element-242" />
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif font-bold tracking-tight mb-6 uppercase leading-tight" data-id="element-243">
              Regalá una <br data-id="element-244" />
              <span className="text-rotterdam-gold pearl-text" data-id="element-245">
                Experiencia Exclusiva
              </span>
            </h1>
          </motion.div>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="text-lg md:text-xl text-rotterdam-cream/75 max-w-2xl mx-auto mb-12 leading-relaxed font-light" data-id="element-246">
            Sorprendé a esa persona especial con una Gift Card de nuestra
            barbería. Viví una experiencia de estilo y cuidado personal, donde
            cada detalle está pensado para brindar un momento único.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} data-id="element-247">
            <a href="#cards" className="inline-block px-10 py-5 pearl-bg text-rotterdam-black font-bold tracking-wider text-sm rounded-full hover:opacity-90 transition-all duration-300 shadow-[0_0_30px_rgba(107,184,212,0.25)] hover:shadow-[0_0_50px_rgba(107,184,212,0.45)]" data-id="element-248">
              QUIERO REGALAR
            </a>
          </motion.div>
        </div>
      </section>

      {/* CARDS GRID */}
      <section id="cards" className="py-24 px-6 bg-[#0A1A14]" data-id="element-249">
        <div className="max-w-6xl mx-auto" data-id="element-250">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="text-center mb-16" data-id="element-251">
            <h2 className="text-3xl md:text-5xl font-serif font-bold mb-4" data-id="element-252">
              Nuestras Gift Cards
            </h2>
            <p className="text-rotterdam-gold pearl-text font-serif italic text-lg" data-id="element-253">
              Elegí la experiencia perfecta
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-254">
            {giftCards.map((card, index) => <motion.div key={card.id} initial={{
            opacity: 0,
            y: 40
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.6,
            delay: index * 0.1
          }} whileHover={{
            y: -8,
            transition: {
              duration: 0.3
            }
          }} className="flex flex-col group cursor-default" data-id="element-255">
                {/* Vertical card — 9:16 portrait */}
                <div className="relative w-full rounded-2xl overflow-hidden border border-[#6BB8D4]/25 flex flex-col justify-between p-7" style={{
              ...leatherStyle,
              aspectRatio: '9/16',
              boxShadow: '0 8px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(107,184,212,0.08)',
              transition: 'box-shadow 0.4s ease'
            }} data-id="element-256">
                  {/* Hover glow border */}
                  <div className="absolute inset-0 rounded-2xl border border-rotterdam-gold/0 group-hover:border-rotterdam-gold/50 transition-all duration-500 pointer-events-none" style={{
                boxShadow: '0 0 0 0 transparent'
              }} data-id="element-257" />

                  {/* Top: GIFT CARD label + icon */}
                  <div className="relative z-10 flex justify-between items-start" data-id="element-258">
                    <span className="text-rotterdam-gold pearl-text font-serif font-bold tracking-[0.2em] text-xs uppercase" data-id="element-259">
                      Gift Card
                    </span>
                    <card.icon className="w-5 h-5 text-rotterdam-gold pearl-icon opacity-70" data-id="element-260" />
                  </div>

                  {/* Middle: Brand + circular seal */}
                  <div className="relative z-10 flex flex-col items-center text-center gap-4" data-id="element-261">
                    <div className="w-16 h-16 rounded-full border-2 border-rotterdam-gold/40 flex items-center justify-center" style={{
                  boxShadow: '0 0 12px rgba(107,184,212,0.2)'
                }} data-id="element-262">
                      <span className="font-serif text-rotterdam-gold pearl-text text-2xl italic font-bold" data-id="element-263">
                        R
                      </span>
                    </div>
                    <div data-id="element-264">
                      <p className="font-serif text-rotterdam-cream/50 text-xs tracking-widest uppercase mb-1" data-id="element-265">
                        Rotterdam
                      </p>
                      <h4 className="font-serif text-white text-xl font-bold leading-tight" data-id="element-266">
                        Barbería
                      </h4>
                    </div>
                    {/* Divider */}
                    <div className="w-16 h-px bg-rotterdam-gold/40" data-id="element-267" />
                    {/* Service name */}
                    <h3 className="font-serif text-rotterdam-cream font-semibold text-base leading-snug px-2" data-id="element-268">
                      {card.title}
                    </h3>
                    <p className="text-rotterdam-cream/50 text-xs leading-relaxed px-2" data-id="element-269">
                      {card.desc}
                    </p>
                  </div>

                  {/* Bottom: Premium label + consult button or price */}
                  <div className="relative z-10 flex flex-col items-center gap-3" data-id="element-270">
                    {card.isConsult ? <a href="https://wa.link/ybqcdw" target="_blank" rel="noopener noreferrer" className="px-5 py-2 border border-rotterdam-gold/60 text-rotterdam-gold pearl-text text-xs font-bold tracking-widest uppercase rounded-full hover:bg-rotterdam-gold/10 transition-colors" data-id="element-271">
                        Consultanos
                      </a> : <span className="text-rotterdam-gold pearl-text font-bold text-sm tracking-wider" data-id="element-272">
                        {card.price}
                      </span>}
                    <span className="text-rotterdam-cream/30 text-xs tracking-widest uppercase" data-id="element-273">
                      Premium Service
                    </span>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* FOOTER CTA */}
      <section className="py-20 px-6 text-center" style={{
      background: '#0A1A14'
    }} data-id="element-274">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-275">
          <h3 className="text-2xl md:text-3xl font-serif font-bold mb-4" data-id="element-276">
            ¿Querés regalar una experiencia?
          </h3>
          <p className="text-rotterdam-cream/60 mb-8 max-w-md mx-auto" data-id="element-277">
            Contactanos por WhatsApp y te ayudamos a elegir la gift card
            perfecta.
          </p>
          <a href="https://wa.link/ybqcdw" target="_blank" rel="noopener noreferrer" className="inline-block px-8 py-4 bg-green-600 hover:bg-green-500 text-white font-bold rounded-full transition-colors duration-300" data-id="element-278">
            Escribinos por WhatsApp
          </a>
        </motion.div>
      </section>
    </div>;
}